<template>
    <div class="challenges-table">
        <v-data-table-server
            :items="items"
            :headers="headers"
            :loading="loading"
            :loading-text="$t('common.loading')"
            :no-data-text="$t('common.noDataText')"
            :items-per-page-text="`${$t('common.rowsPerPage')}`"
            :page-text="`${currentPage} ${$t('common.of')} ${lastPage}`"
            :items-per-page-options="itemsPerPageOptions"
            :items-length="total"
            :options="options"
            fixed-header
            :height="items.length > 10 ? '60vh' : '100%'"
            @update:options="optionsUpdate"
        >
            <template #loading>
                <div class="challenges-table__loading">{{ $t('common.loading') }}</div>
            </template>

            <template #top>
                <div class="kasanowa-top-filter-wrapper">
                    <KTextField
                        id="search"
                        v-model="search"
                        name="search"
                        label=""
                        :placeholder="$t('common.search')"
                        hide-details
                        clearable
                    />
                </div>
            </template>

            <template #item.name="elem">
                <div v-if="elem.item.name != null">{{ elem.item.name }}</div>
                <div v-else>-</div>
            </template>

            <template #item.date_from="elem">
                <div v-if="elem.item.start_at != null">{{ useDataFormat(elem.item.start_at) }}</div>
                <div v-else>-</div>
            </template>

            <template #item.date_to="elem">
                <div v-if="elem.item.end_at != null">{{ useDataFormat(elem.item.end_at) }}</div>
                <div v-else>-</div>
            </template>

            <template #footer.prepend>
                <div class="kasanowa-total-table-counter">{{ $t('common.total') }}: {{ total }}</div>
            </template>
        </v-data-table-server>
    </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { itemsPerPageOptions } from '@/hooks/TableSettingsHooks';
import ChallengeApi from '@/api/v1/ChallengeApi';
import { toast } from 'vue3-toastify';
import { useDataFormat } from '@/hooks/DataHooks';
import type { ITableHeader, ITableOptions } from '@/models/TableSettingsModel';
import type { IChallengesItemList } from '@/models/ChallengesModel';
import KTextField from '@/components/atoms/KTextField.vue';

const headers = ref<ITableHeader[]>([
    {
        title: t('challenges.name'),
        align: 'start',
        sortable: false,
        key: 'name',
        width: '280px',
        minWidth: '280px',
    },
    {
        title: t('challenges.dateFrom'),
        align: 'center',
        sortable: false,
        key: 'date_from',
        width: '120px',
        minWidth: '120px',
    },
    {
        title: t('challenges.dateTo'),
        align: 'center',
        sortable: false,
        key: 'date_to',
        width: '120px',
        minWidth: '120px',
    },
]);
const loading = ref<boolean>(false);
const currentPage = ref<number>(0);
const lastPage = ref<number>(0);
const total = ref<number>(0);
const items = ref<IChallengesItemList[]>([]);
const challengeApi = new ChallengeApi();
const challengeList = async() => {
    loading.value = true;
    try {
        const result = await challengeApi.challengeList(
            options.value.page,
            options.value.itemsPerPage,
            options.value.sortBy.length > 0 ? options.value.sortBy[0].key : null,
            options.value.sortBy.length > 0 ? options.value.sortBy[0].order : null,
            search.value,
        );
        if (!result.isSuccess) {
            toast.error(t('challenges.toast.errorChallengesList'));
            loading.value = false;
            return;
        }
        currentPage.value = result.payload.current_page;
        lastPage.value = result.payload.last_page;
        total.value = result.payload.total;
        items.value = result.payload.data;
    } catch (e) {
        toast.error(t('challenges.toast.errorChallengesList'));
        loading.value = false;
        return;
    }
    loading.value = false;
};
const options = ref<ITableOptions>({
    page: 1,
    itemsPerPage: 10,
    sortBy: [],
    groupBy: [],
    search: null,
});
const optionsUpdate = (val:ITableOptions) => {
    options.value.page = val.page;
    options.value.itemsPerPage = val.itemsPerPage;
    options.value.search = val.search;
    options.value.groupBy = val.groupBy;
    options.value.sortBy = val.sortBy;
    challengeList();
};

const search = ref<string | null>(null);
const searchInterval = ref<any>(undefined);
watch(() => search.value, (newValue, oldValue) => {
    if (newValue !== oldValue) {
        clearTimeout(searchInterval.value);
    }
    searchInterval.value = setTimeout(() => {
        challengeList();
    }, 700);
}, {
    deep: true
});
</script>

<style scoped lang="scss">
.challenges-table {
    &__loading {
        opacity: 0.6;
    }

    &__action-wrapper {
        display: flex;
        justify-content: space-around;
    }

    &__icon-wrapper {
        padding: 5px;
        height: 26px;
    }

    &__btn-dialog-wrapper {
        margin-top: 10px;
        display: flex;
        flex-direction: column;
        gap: 10px;

        @include media-breakpoint-up(sm) {
            margin-top: 20px;
            flex-direction: row;
            width: 100%;
            justify-content: space-around;
        }
    }

    &__dialog-title {
        font-size: 18px;
        letter-spacing: 0;
    }
}
</style>
